import React, { FC, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  AutoCompleteInput,
  Button,
  CheckBox,
  Form,
  Grid,
  Input,
  Picker,
  Row,
  CurrencyInput
} from 'components'
import { MagnifyingGlassIcon } from 'components/Icons'
import { useDebounce, useLoading, useModal, useTabs } from 'hooks'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import {
  CentroPlantaEntity,
  CostCenterServices,
  IAutoCompleteCostCentersBody
} from 'services/NGT/CostCenter'
import {
  ContractServices,
  ContratoAutocompleteResponseDTO,
  IGetRecordSheetResponse,
  INewAndUpdateRecordSheet,
  RecordSheetCRUDServices
} from 'services/NGT/RecordsSheet'
import { COLORS, SPACING } from 'utils'
import { yup } from 'utils/yup'

import { ITypes } from './types'
import { Message } from '@mui/icons-material'

const schema = yup.object({
  anoCompetencia: yup
    .string()
    .required('Campo Obrigatório')
    .typeError('Insira uma data de Competência válida.'),
  cdContrato: yup
    .string()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório'),
  dsFormaPagamento: yup
    .string()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório'),
  dsTipoDocumento: yup
    .string()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório'),
  dtVencimento: yup
    .date()
    .min(moment(), 'Data de vencimento deve ser maior do que a data atual')
    .required('Campo Obrigatório')
    .typeError('Insire uma data valida.'),
  flBloqPagamento: yup.boolean().nullable(),
  flImportadoSAP: yup.boolean().nullable(),
  flArquivado: yup.boolean().nullable(),
  idFolhaRegistro: yup.number(),
  nmCentroPlanta: yup
    .string()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório'),
  txDescricao: yup
    .string()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório'),
  txNumeroDocumento: yup
    .string()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório'),
  vlLiquido: yup
    .number()
    .required('Campo Obrigatório')
    .typeError('Campo Obrigatório')
})

export const AddRecordSheet: FC = () => {
  const { tabNavigate, handleChangeTab, handleChangeTabName } = useTabs()
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const {
    control,
    watch,
    reset,
    handleSubmit,
    setValue,
    formState: { errors, isValid }
  } = useForm<ITypes>({
    defaultValues: {
      anoCompetencia: null,
      cdContrato: '',
      cdFolhaRegistroSRV: '',
      centroPlantaId: null,
      contratoId: 0,
      dsFormaPagamento: '',
      dsTipoDocumento: '',
      dtVencimento: null,
      flBloqPagamento: false,
      flImportadoSAP: false,
      flArquivado: false,
      flLegado: false,
      idFolhaRegistro: 0,
      nmCentroPlanta: '',
      numeroLinha: '1',
      txDescricao: '',
      txDocumento: '',
      txNumeroDocumento: 0,
      vlLiquido: 0
    },
    resolver: yupResolver(schema)
  })
  const [recordSheet, setRecordSheet] =
    useState<IGetRecordSheetResponse>(undefined)

  const [contratosArray, setContratosArray] = useState<
    ContratoAutocompleteResponseDTO[]
  >([])
  const [selectedContrato, setSelectContrato] =
    useState<ContratoAutocompleteResponseDTO>()
  const [selectedCdContrato, setSelectedCdContrato] = useState<string>()
  const [oldIdFr, setOldIdFr] = useState<string>()

  const [costCenters, setCostCenters] = useState<CentroPlantaEntity[]>([])
  const [flBloqueioPagamento, setFlBloqueioPagamento] = useState<boolean>(false)
  const [flImportadoSAP, setFlImportadoSAP] = useState<boolean>(false)
  const [flArquivado, setArquivado] = useState<boolean>(false)
  const [centroPlantaName, setCentroPlantaName] = useState<string>()
  const [habilitarEdicao, setHabilitarEdicao] = useState<boolean>(false)
  const [hasErrors, setHasErrors] = useState<boolean>(false)

  const idFolhaRegistro = watch('idFolhaRegistro')
  const txNumeroDocumento = watch('cdFolhaRegistroSRV')
  const flLegado = watch('flLegado')

  const handleChangeInput = (value, field) => setValue(field, value)

  const getCostCenter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCostCenters([])
    const requestBody: IAutoCompleteCostCentersBody = {
      hasDinamicSearch: true,
      nmCentroPlanta: e.target.value
    }
    CostCenterServices()
      .AutoCompleteCostCenters(requestBody)
      .then(({ data }) => {
        setCostCenters(data.entity)
      })
  }

  const getRecordSheet = () => {
    setLoading('loading')

    RecordSheetCRUDServices()
      .GetFolhaRegistro(txNumeroDocumento.toString())
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Não foi possivel buscar a Folha de Registro.')
        }

        if (response.data.length != 0) {
          setHabilitarEdicao(false)
          setRecordSheet(response.data[0])
          setFormValues(response.data[0])
        } else {
          handleChangeTabName('Nova Folha de Registro')

          handleClearForm()

          showModal({
            handleConfirm: () => {
              setHabilitarEdicao(true)
              setValue('cdFolhaRegistroSRV', txNumeroDocumento || oldIdFr)
            },
            message: `Não encontramos a folha registro com o numero ${oldIdFr}, gostaria de cadastra-la ?`,
            txtButtonCancel: 'Não',
            txtButtonConfirm: 'Sim',
            type: 'alert'
          })
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            'Ocorreu um erro e não foi possível encontrar os dados pesquisados',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const handleContratoAutoComplete = (event) => {
    setContratosArray([])
    setLoading('loading')

    const cdContrato = String(event.target.value).includes('%')
      ? event.target.value
      : `${event.target.value}%`

    ContractServices()
      .GetContractByContrato(cdContrato)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(
            response.data.mensagem ||
              'Não foi possivel buscar a Folha de Registro.'
          )
        }

        setContratosArray(response.data.entity)

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            'Ocorreu um erro e não foi possível encontrar os dados pesquisados',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const deleteRecordSheet = () => {
    setLoading('loading')

    RecordSheetCRUDServices()
      .DeleteRecordSheet(idFolhaRegistro)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Não possivel excluir a Folha de Registro.')
        }

        setLoading('idle')

        showModal({
          hideButtonCancel: true,
          message: 'Folha de Registro excluida com sucesso!',
          txtButtonConfirm: 'Fechar',
          type: 'success'
        })
        handleClearForm()
        tabNavigate.goBack()
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            'Ocorreu um erro e não foi possível deletar esta Folha de Registro.',
          txtButtonConfirm: 'Fechar',
          type: 'alert'
        })
      })
  }

  const setFormValues = (recordSheetData: IGetRecordSheetResponse) => {
    
    if (recordSheetData) {
      handleChangeInput(recordSheetData?.anoCompetencia, 'anoCompetencia')
      handleChangeInput(recordSheetData?.cdContrato, 'cdContrato')
      handleChangeInput(
        recordSheetData?.cdFolhaRegistroSRV,
        'cdFolhaRegistroSRV'
      )
      handleChangeInput(recordSheetData?.contratoId, 'contratoId')
      handleChangeInput(recordSheetData?.dsFormaPagamento, 'dsFormaPagamento')
      handleChangeInput(recordSheetData?.dsTipoDocumento, 'dsTipoDocumento')
      handleChangeInput(
        recordSheetData?.dtUltAtualizacaoSap,
        'dtUltAtualizacaoSap'
      )
      handleChangeInput(
        recordSheetData?.dtVencimento === '0001-01-01T00:00:00'
          ? null
          : recordSheetData?.dtVencimento,
        'dtVencimento'
      )
      handleChangeInput(recordSheetData?.flBloqPagamento, 'flBloqPagamento')
      handleChangeInput(recordSheetData?.flImportadoSAP, 'flImportadoSAP')
      handleChangeInput(recordSheetData?.flLegado, 'flLegado')
      handleChangeInput(recordSheetData?.nmCentroPlanta, 'nmCentroPlanta')
      setCentroPlantaName(recordSheetData?.nmCentroPlanta)
      handleChangeInput(recordSheetData?.nmCentroPlanta, 'centroPlantaId')
      handleChangeInput(recordSheetData?.numeroLinha, 'numeroLinha')
      handleChangeInput(recordSheetData?.txDescricao, 'txDescricao')
      handleChangeInput(recordSheetData?.txDocumento, 'txDocumento')
      handleChangeInput(recordSheetData?.txNumeroDocumento, 'txNumeroDocumento')
      handleChangeInput(
        recordSheetData?.cdFolhaRegistroSRV,
        'cdFolhaRegistroSRV'
      )
      handleChangeInput(recordSheetData?.vlLiquido, 'vlLiquido')
      handleChangeInput(recordSheetData?.idFolhaRegistro, 'idFolhaRegistro')
      setFlImportadoSAP(recordSheetData?.flImportadoSAP)
      setFlBloqueioPagamento(recordSheetData?.flBloqPagamento)
      setArquivado(recordSheetData?.flArquivado)
    }
  }

  const handleSubmitForm = async (form: INewAndUpdateRecordSheet | any) => {
    setLoading('loading')

    const obj: INewAndUpdateRecordSheet = {
      ...form,
      anoCompetencia: moment(form?.anoCompetencia).toISOString(),
      contratoId:
        recordSheet && habilitarEdicao
          ? form?.contratoId
          : selectedContrato?.idContrato,
      dtVencimento: moment(form?.dtVencimento).toISOString(),
      flBloqPagamento: flBloqueioPagamento,
      idFolhaRegistro:
        recordSheet && habilitarEdicao ? form?.idFolhaRegistro : 0,
      nmCentroPlanta: centroPlantaName,
      vlLiquido: parseFloat(
        String(form?.vlLiquido).replace('R$ ', '').replace(',', '.')
      )
    }

    if (recordSheet && habilitarEdicao) {
      RecordSheetCRUDServices()
        .UpdateRecordSheet(obj)
        .then((response) => {
          if (response.status !== 200 || response.data.mensagem) {
            throw new Error(
              response.data.mensagem || 'Não foi possivel atualizar o Contrato.'
            )
          }

          showModal({
            handleConfirm: () => {
              handleClearForm()
              tabNavigate.goBack()
            },
            hideButtonCancel: true,
            message: 'Folha de registro atualizada com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })

          handleChangeTab(null, 0)
          setLoading('idle')
        })
        .catch((error: Error) => {
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: 'Não foi possível atualizar esta Folha de Registro.',
            txtButtonConfirm: 'Fechar',
            type: 'alert'
          })
        })
    } else {
      if (moment(obj.dtVencimento) < moment()) {
        setLoading('idle')
        return
      }
      RecordSheetCRUDServices()
        .CreateRecordSheet(obj)
        .then((response) => {
          if (response.status !== 200 || response.data?.title) {
            throw new Error(
              response.data?.title ||
                'Não foi possivel salvar a Folha de registro.'
            )
          }

          showModal({
            handleConfirm: () => {
              handleClearForm()
              tabNavigate.goBack()
            },
            hideButtonCancel: true,
            message: 'Folha de registro criada com sucesso.',
            txtButtonConfirm: 'OK',
            type: 'success'
          })

          handleChangeTab(null, 0)
          setLoading('idle')
        })
        .catch((error: Error) => {
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: error.message || 'Não foi possível salvar esta nova Folha de Registro.',
            txtButtonConfirm: 'Fechar',
            type: 'alert'
          })
        })
    }
  }

  const handleSelectedValue = (selectedValue, value) => {
    setSelectedCdContrato(selectedValue)

    const contratoObj: ContratoAutocompleteResponseDTO = contratosArray.filter(
      (contrato) => contrato.cdContrato === selectedValue
    )[0]

    setSelectContrato(contratoObj)
    setValue('cdContrato', selectedValue)
    setCentroPlantaName(contratoObj.txDescricaoCentroPlanta)
    setValue('centroPlantaId', contratoObj.centroPlantaId)
    setValue('nmCentroPlanta', contratoObj.txDescricaoCentroPlanta)
    return
  }

  const handleClearForm = () => {
    reset()
    setSelectedCdContrato(undefined)
    setSelectContrato(undefined)
    setContratosArray([])
    setCentroPlantaName(undefined)
    setRecordSheet(undefined)
    setValue('dtVencimento', null)
    setValue('numeroLinha', '')
    setValue('anoCompetencia', null)
    setValue('vlLiquido', 0)
    setValue('idFolhaRegistro', 0)
    setValue('contratoId', 0)
    setFlBloqueioPagamento(false)
    setFlImportadoSAP(false)
    setArquivado(false)
  }

  useEffect(() => {
    if (txNumeroDocumento != null && txNumeroDocumento != undefined) {
      setOldIdFr(txNumeroDocumento)
    }
  }, [txNumeroDocumento])

  /* UseEffect para mudança do nome da Tab
    e Controle do habilitar edição */
  useEffect(() => {
    if (txNumeroDocumento && recordSheet) {
      if (flImportadoSAP || flLegado) {
        handleChangeTabName(`Visualização - FR ${txNumeroDocumento}`)
        setHabilitarEdicao(false)
      } else {
        handleChangeTabName(`Atualização - FR ${txNumeroDocumento}`)
        setHabilitarEdicao(true)
      }
    } else {
      handleChangeTabName('Nova Folha de Registro')
    }
  }, [recordSheet, txNumeroDocumento, flImportadoSAP, flLegado])

  return (
    <Form
      onSubmit={handleSubmit((form) => {
        if (!isValid) {
          setHasErrors(false)
        } else {
          setHasErrors(true)
        }

        handleSubmitForm(form)
      })}
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      padding={SPACING.spacing20}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <Grid
        gridCols="1fr 1fr 1fr 1.5fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <Input
          control={control}
          errors={errors}
          label="Código Folha Registro"
          placeholder="Nº"
          formValue={'cdFolhaRegistroSRV'}
          disabled={recordSheet ? true : false}
          onClickIcon={() => (!recordSheet ? getRecordSheet() : {})}
          inputIcon={<MagnifyingGlassIcon />}
        />
        <Input
          control={control}
          errors={errors}
          label="Nº Documento"
          placeholder="Nº"
          formValue={'txNumeroDocumento'}
          disabled={recordSheet ? true : false}
          maxLength={49}
        />
        <AutoCompleteInput
          name="codigoContrato"
          disabled={
            (!recordSheet && !habilitarEdicao) || recordSheet ? true : false
          }
          marginTop="0.25rem"
          onChangeText={useDebounce(handleContratoAutoComplete, 600)}
          value={selectedCdContrato || watch('cdContrato') || null}
          options={contratosArray.map((contrato) => ({
            label: contrato?.cdContrato,
            value: contrato?.idContrato
          }))}
          optionValue="label"
          label="Contrato"
          noFilter
          valueName="value"
          onChange={(selectedValue, value) => {
            handleSelectedValue(selectedValue, value)
          }}
        />
        <Input
          control={control}
          errors={errors}
          label="Tipo Documento"
          placeholder="Tipo Documento"
          formValue="dsTipoDocumento"
          disabled={!habilitarEdicao}
          maxLength={49}
        />
        <AutoCompleteInput
          marginTop="0.25rem"
          value={centroPlantaName}
          onChange={(selectedName, value) => {
            setCentroPlantaName(selectedName)
            setValue('nmCentroPlanta', selectedName)
          }}
          onChangeText={(event) => {
            setCostCenters([])
            getCostCenter(event)
          }}
          noFilter
          label="Centro Planta"
          options={costCenters}
          key="CentroPlantaInput"
          optionValue="nmCentroPlanta"
          valueName="idCentroPlanta"
          disabled={true}
        />
      </Grid>
      <Grid
        gridCols="1fr 1fr 2fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <Picker
          defaultValue={null}
          label="Vencimento"
          formValue="dtVencimento"
          errors={errors}
          control={control}
          disabled={!habilitarEdicao}
          minDate={recordSheet && habilitarEdicao && Date.now()}
        />
        <Picker
          control={control}
          errors={errors}
          views={['month', 'year']}
          placeholder="mm/aaaa"
          label="Competência"
          formValue="anoCompetencia"
          type={'monthAndYear'}
          disabled={!habilitarEdicao}
        />
        <Input
          control={control}
          errors={errors}
          label="Descrição Folha Registro"
          placeholder="Descrição Folha Registro"
          formValue="txDescricao"
          maxLength={130}
          disabled={!habilitarEdicao}
        />
      </Grid>
      <Grid
        gridCols="1fr 1fr 0.5fr 1fr 1fr 0.5fr"
        alignItems="center"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`0 0 ${SPACING.spacing30} 0`}
      >
        <CurrencyInput
          control={control}
          errors={errors}
          label="Valor Líquido"
          placeholder="Valor Líquido"
          formValue="vlLiquido"
          maxLength={20}
          disabled={!habilitarEdicao}
        />
        <Input
          control={control}
          errors={errors}
          label="Forma Pagamento"
          placeholder="Forma Pagamento"
          formValue="dsFormaPagamento"
          disabled={!habilitarEdicao}
          maxLength={49}
        />
        <Row justifyContent={'center'}>
          <Button
            text="Deletar"
            onClick={() =>
              showModal({
                handleConfirm: () => deleteRecordSheet(),
                message: `Tem certeza de que deseja apagar a FR ${txNumeroDocumento}?`,
                txtButtonCancel: 'NÃO',
                txtButtonConfirm: 'SIM',
                type: 'alert'
              })
            }
            disabled={!habilitarEdicao || !recordSheet}
          />
        </Row>
        <CheckBox
          marginTop="1rem"
          label="Importado pelo SAP"
          checked={flImportadoSAP}
          onClick={(event) => {
            handleChangeInput(event.target.checked, 'flImportadoSAP')
            setFlImportadoSAP((prevState) => !prevState)
          }}
          disabled
        />
        <CheckBox
          marginTop="1rem"
          label="Pagamento Bloqueado"
          checked={flBloqueioPagamento}
          disabled
        />
        <CheckBox 
        
          
          label="Arquivado"
          checked={flArquivado}
          disabled
        />
      </Grid>
      <Row justifyContent="flex-end">
     
        <Button
          text="Voltar"
          variantButton="outlined"
          marginRight={SPACING.spacing20}
          onClick={tabNavigate.goBack}
        />
        <Button text="Limpar" variantButton="text" onClick={handleClearForm} />
        {recordSheet && habilitarEdicao ? (
          <Button
            text="Atualizar"
            type="submit"
            marginLeft={SPACING.spacing20}
            disabled={!habilitarEdicao}
          />
        ) : (
          <Button
            text="Salvar"
            type="submit"
            marginLeft={SPACING.spacing20}
            disabled={!habilitarEdicao}
          />
        )}
      </Row>
    </Form>
  )
}
